

























































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState, SortOrder, SortSpecs } from '@/store/types/general.types';
import {
  EventStatus,
  Group,
  Pagination
} from '@/store/modules/admin/types/admin.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import EventsList from './components/EventsList.vue';
import {
  JbicEventParams,
  Event
} from '@/store/modules/admin/types/admin.types';
import { toTitleCase } from '@/jbi-shared/util/string.utils';
import { useWebsocket } from '@/utils/websocket.util';
import { subscribeToRealTime } from '@/utils/real-time.util';

const getInitFormParams = (): JbicEventParams => ({
  groupName: 'All',
  eventName: '',
  status: 'All',
  page: 1,
  limit: 50,
  sortColumn: 'event.updated_at',
  sortOrder: SortOrder.DESC
});

@Component({
  components: {
    BasePaginatorHoc,
    EventsList
  }
})
export default class EventsTab extends Vue {
  // get jbic groups
  @Action('admin/getJbicEventsWithPagination')
  getJbicEventsWithPagination!: (params?: JbicEventParams) => void;

  @State(({ admin }: RootState) => admin.apiState.getJbicEventsWithPagination)
  jbicEventsWithPaginationApiState!: ApiState;

  @State(({ admin }: RootState) => admin.jbicEventsWithPagination)
  jbicEventsWithPagination!: Pagination<Event>;

  get EventsList() {
    return EventsList;
  }

  get StatusList() {
    return Object.values(EventStatus)
      .map((status) => toTitleCase(status, '_'))
      .sort();
  }

  // pagination
  public perPage = 50;
  public page = 1;
  public sortColumn: string = 'name';
  public sortOrder: SortOrder = SortOrder.ASC;
  public totalItems: number = 0;

  // filter
  public filterParams: JbicEventParams = getInitFormParams();

  // params
  public groups: string[] = [];
  public events: Event[] = [];
  public eventStatusValues: string[] = this.StatusList;

  private unsubscribeRealtime: (() => void) | undefined;

  async mounted() {
    this.getJbicEventsWithPaginationHandler();
    this.unsubscribeRealtime = await subscribeToRealTime.call(
      this,
      'groupEvent',
      async () => {
        this.getJbicEventsWithPaginationHandler();
      }
    );
  }

  beforeDestroy() {
    if (this.unsubscribeRealtime) {
      this.unsubscribeRealtime(); // remove listener
      const { disconnectWs } = useWebsocket.call(this);
      disconnectWs();
    }
  }

  getJbicEventsWithPaginationHandler() {
    const groupName: string =
      this.filterParams.groupName === 'All'
        ? ''
        : this.filterParams.groupName ?? '';
    const status: string =
      this.filterParams.status === 'All' ? '' : this.filterParams.status ?? '';

    this.getJbicEventsWithPagination({
      groupName,
      eventName: this.filterParams.eventName,
      status,
      limit: this.filterParams.limit,
      page: this.filterParams.page,
      sortColumn: this.filterParams.sortColumn,
      sortOrder: this.filterParams.sortOrder
    });
  }

  resetFilter() {
    this.filterParams = getInitFormParams();
  }

  @Watch('jbicEventsWithPaginationApiState')
  onJbicEventsWithPaginationApiState() {
    if (
      !this.jbicEventsWithPaginationApiState ||
      this.jbicEventsWithPaginationApiState.loading ||
      !this.jbicEventsWithPaginationApiState.success ||
      !this.jbicEventsWithPagination
    ) {
      return;
    }

    this.events = this.jbicEventsWithPagination.items;
    this.totalItems = this.jbicEventsWithPagination.totalItems;

    this.groups = [];
    this.events.forEach((item) => {
      const currentGroup: Group = item.group;
      const isExist: boolean = this.groups.includes(currentGroup.name);

      if (currentGroup && !isExist) {
        this.groups.push(currentGroup.name);
      }
    });
    this.groups.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }

  @Watch('filterParams', { deep: true })
  @Debounce(500)
  onFilterParams() {
    this.getJbicEventsWithPaginationHandler();
  }

  public async handleSort(data: SortSpecs) {
    this.sortColumn = data.sortColumn;
    this.sortOrder = data.sortOrder;

    this.getJbicEventsWithPaginationHandler();

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Groups',
        groupName: this.$route.query.groupName || this.filterParams.groupName!,
        eventName: this.$route.query.eventName || this.filterParams.eventName!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: (this.$route.query.limit as string) || this.perPage.toString(),
        page: (this.$route.query.page as string) || this.page.toString(),
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder
      }
    });
  }

  public async handlePaginator({
    perPage,
    page
  }: {
    perPage: number;
    page: number;
  }) {
    if (this.$route.query.tab !== 'Groups') {
      return;
    }
    this.perPage = perPage || +this.$route.query.limit;
    this.page = page || +this.$route.query.page;

    this.getJbicEventsWithPaginationHandler();

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Groups',
        groupName: this.$route.query.groupName || this.filterParams.groupName!,
        eventName: this.$route.query.eventName || this.filterParams.eventName!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
      }
    });
  }
}
